import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { Debug } from '@sentry/integrations';
import * as SentryLib from '@sentry/node';
import { ddIgnorableErrors } from '~/constants/ddIgnorableErrors';

// this import will be replaced with `@sentry/browser` due to next.config.js

/**
 * Type definition for SentryConfig
 * @typedef {Object} SentryConfig
 * Type definition for DatadogConfig
 * @typedef {Object} DatadogConfig
 * @property {Object} Sentry - Sentry instance properly setup
 * @property {Object} Datadog - Datadog instance properly setup
 * @property {Function} captureException - Callback to capture exceptions manually
 */

/**
 * Configs everything related to Sentry and Datadog service.
 *
 * Inspired by sentry example from Next JS
 * https://github.com/zeit/next.js/blob/canary/examples/with-sentry/utils/sentry.js
 *
 * From Datadog documentation
 * https://docs.datadoghq.com/real_user_monitoring/guide/monitor-your-nextjs-app-with-rum/?tab=npm#instrument-your-application
 * https://docs.datadoghq.com/real_user_monitoring/browser/collecting_browser_errors/?tab=npm#react-error-boundaries-instrumentation
 *
 * @returns {SentryConfig, DatadogConfig}
 */
const SENTRY_DSN = process.env.NEXT_PUBLIC_SENTRY_DSN;
const SENTRY_ENVIRONMENT = process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT;
const SENTRY_RELEASE = process.env.NEXT_PUBLIC_SENTRY_RELEASE;

const isSentryReady = Boolean(
  SENTRY_DSN && SENTRY_ENVIRONMENT && SENTRY_RELEASE,
);

const sentryOptions = {
  attachStacktrace: true,
  autoSessionTracking: false,
  dsn: SENTRY_DSN,
  environment: SENTRY_ENVIRONMENT,
  ignoreErrors: ['KRI-IGNORE'],
  maxBreadcrumbs: 50,
  release: SENTRY_RELEASE,
  sendDefaultPii: true,
};

const DATADOG_APP_ID = process.env.NEXT_PUBLIC_DATADOG_APP_ID;
const DATADOG_TOKEN = process.env.NEXT_PUBLIC_DATADOG_TOKEN;
const DATADOG_ENV = process.env.NEXT_PUBLIC_DATADOG_ENV;
const GIT_TAG = process.env.NEXT_PUBLIC_GIT_TAG;

/**
 * Detects if the current user-agent is a bot based on known bot patterns.
 * Based on Datadog documentation:
 * https://docs.datadoghq.com/real_user_monitoring/guide/identify-bots-in-the-ui/#filter-out-bot-sessions-on-intake
 *
 * @returns {boolean} True if the user-agent matches a bot pattern, false otherwise
 */
const isBotSession = () => {
  if (typeof window === 'undefined' || !navigator?.userAgent) {
    return false;
  }

  // Regex patterns to identify known bot instances
  // HeadlessChrome is Chrome running in headless mode, typically used for automated testing,
  // web scraping, or CI/CD pipelines (e.g., Cypress, Puppeteer, Playwright)
  const botPattern =
    '(googlebot/|bot|Googlebot-Mobile|Googlebot-Image|Google favicon|Mediapartners-Google|bingbot|slurp|java|wget|curl|Commons-HttpClient|Python-urllib|libwww|httpunit|nutch|phpcrawl|msnbot|jyxobot|FAST-WebCrawler|FAST Enterprise Crawler|biglotron|teoma|convera|seekbot|gigablast|exabot|ngbot|ia_archiver|GingerCrawler|webmon |httrack|webcrawler|grub.org|UsineNouvelleCrawler|antibot|netresearchserver|speedy|fluffy|bibnum.bnf|findlink|msrbot|panscient|yacybot|AISearchBot|IOI|ips-agent|tagoobot|MJ12bot|dotbot|woriobot|yanga|buzzbot|mlbot|yandexbot|purebot|Linguee Bot|Voyager|CyberPatrol|voilabot|baiduspider|citeseerxbot|spbot|twengabot|postrank|turnitinbot|scribdbot|page2rss|sitebot|linkdex|Adidxbot|blekkobot|ezooms|dotbot|Mail.RU_Bot|discobot|heritrix|findthatfile|europarchive.org|NerdByNature.Bot|sistrix crawler|ahrefsbot|Aboundex|domaincrawler|wbsearchbot|summify|ccbot|edisterbot|seznambot|ec2linkfinder|gslfbot|aihitbot|intelium_bot|facebookexternalhit|yeti|RetrevoPageAnalyzer|lb-spider|sogou|lssbot|careerbot|wotbox|wocbot|ichiro|DuckDuckBot|lssrocketcrawler|drupact|webcompanycrawler|acoonbot|openindexspider|gnam gnam spider|web-archive-net.com.bot|backlinkcrawler|coccoc|integromedb|content crawler spider|toplistbot|seokicks-robot|it2media-domain-crawler|ip-web-crawler.com|siteexplorer.info|elisabot|proximic|changedetection|blexbot|arabot|WeSEE:Search|niki-bot|CrystalSemanticsBot|rogerbot|360Spider|psbot|InterfaxScanBot|Lipperhey SEO Service|CC Metadata Scaper|g00g1e.net|GrapeshotCrawler|urlappendbot|brainobot|fr-crawler|binlar|SimpleCrawler|Livelapbot|Twitterbot|cXensebot|smtbot|bnf.fr_bot|A6-Indexer|ADmantX|Facebot|Twitterbot|OrangeBot|memorybot|AdvBot|MegaIndex|SemanticScholarBot|ltx71|nerdybot|xovibot|BUbiNG|Qwantify|archive.org_bot|Applebot|TweetmemeBot|crawler4j|findxbot|SemrushBot|yoozBot|lipperhey|y!j-asr|Domain Re-Animator Bot|AddThis|HeadlessChrome)';

  const regex = new RegExp(botPattern, 'i');
  return regex.test(navigator.userAgent);
};

const baseSessionSampleRate = DATADOG_ENV === 'production' ? 100 : 50;
// Set sessionSampleRate to 0 for bot sessions, otherwise use the base rate
const DATADOG_SESSION_SAMPLE_RATE = isBotSession() ? 0 : baseSessionSampleRate;
const DATADOG_REPLAY_SAMPLE_RATE = DATADOG_ENV === 'production' ? 100 : 0;

const isDatadogReady = Boolean(
  DATADOG_APP_ID && DATADOG_TOKEN && DATADOG_ENV && GIT_TAG,
);

const datadogOptions = {
  applicationId: DATADOG_APP_ID || '',
  clientToken: DATADOG_TOKEN || '',
  env: DATADOG_ENV,
  version: GIT_TAG,
  site: 'datadoghq.eu',
  service: 'foam',
  compressIntakeRequests: true,
  sessionSampleRate: DATADOG_SESSION_SAMPLE_RATE,
  sessionReplaySampleRate: DATADOG_REPLAY_SAMPLE_RATE,
  trackUserInteractions: true,
  actionNameAttribute: 'track-id',
  trackResources: true,
  trackLongTasks: true,
  defaultPrivacyLevel: 'allow',
  enableExperimentalFeatures: ['feature_flags'],
  trackSessionAcrossSubdomains: true,
  allowedTracingUrls: [
    process.env.NEXT_PUBLIC_API_RESOURCE,
    process.env.NEXT_PUBLIC_SEARCH_RESOURCE,
    process.env.NEXT_PUBLIC_PDF_RESOURCE,
    process.env.NEXT_PUBLIC_MEDIA_KITS_API,
  ],
  beforeSend: (event) => {
    // discard a RUM error if error message includes any of the ignorable errors
    if (
      event.type === 'error' &&
      ddIgnorableErrors.some((error) => event.error.message.includes(error))
    ) {
      return false;
    }
    return true;
  },
};

// Documentation: https://docs.datadoghq.com/logs/log_collection/javascript/
const datadogLogsOptions = {
  service: 'foam',
  clientToken: DATADOG_TOKEN,
  forwardErrorsToLogs: true,
  sessionSampleRate: DATADOG_SESSION_SAMPLE_RATE,
  site: 'datadoghq.eu',
  env: DATADOG_ENV,
};

const errorsLoggingConfig = () => {
  // debug errors instead of report them for non-production environments
  // or running E2E tests
  if (
    process.env.NODE_ENV !== 'production' ||
    process.env.NEXT_PUBLIC_CYPRESS_TEST_ENV
  ) {
    // Avoid sending the errors to Sentry...
    sentryOptions.beforeSend = () => null;

    // ... and dump the errors to the console instead.
    sentryOptions.integrations = [
      new Debug({
        debugger: false, // Trigger DevTools debugger instead of using console.log
      }),
    ];
  }

  if (isSentryReady) {
    SentryLib.init(sentryOptions);
  }

  if (isDatadogReady) {
    datadogRum.init(datadogOptions);
    datadogLogs.init(datadogLogsOptions);
  }

  /**
   * Captures exception manually
   * @param {Error} err - Error thrown
   * @param {Object} [additionalData] - Extra data for improving error context
   * @param {Object} [additionalData.req] - Next JS request value
   * @param {Object} [additionalData.res] - Next JS response value
   * @param {Object} [additionalData.errorInfo] - Info related to React component
   * * @param {string} [additionalData.level] - Sentry logging level, one of "fatal", "critical", "error", "warning", "log", "info", and "debug".
   * @param {String} [additionalData.query] - Next JS query value
   * @param {Object} [additionalData.pathname] - Next JS pathname value
   * @param {Object} [additionalData.reduxStore] - App redux store
   * @param {Object} [additionalData.tags] - Tags added to Sentry event
   * @param {Boolean} [additionalData.isSupportedBrowser] - Tags added to Sentry event
   * @returns {String} Generated event ID
   */
  const captureException = (err, additionalData) => {
    if (!(err instanceof Error)) {
      return;
    }
    // Sentry error scope configuration
    SentryLib.configureScope((scope) => {
      if (err.message) {
        // De-duplication currently doesn't work correctly for SSR / browser errors
        // so we force deduplication by error message if it is present
        scope.setFingerprint([err.message]);
      }

      if (err.statusCode) {
        scope.setExtra('statusCode', err.statusCode);
      }

      if (additionalData) {
        const {
          errorInfo,
          isSupportedBrowser,
          level,
          pathname,
          query,
          reduxStore,
          req = {},
          res,
          tags,
        } = additionalData;

        if (level) {
          scope.setLevel(level);
        }

        if (res && res.statusCode) {
          scope.setExtra('statusCode', res.statusCode);
        }

        if (typeof window !== 'undefined') {
          scope.setTag('ssr', false);
          scope.setExtra('query', query);
          scope.setExtra('pathname', pathname);

          // TODO: find proper way to set session id from our tokens here
          // https://docs.sentry.io/enriching-error-data/scopes/?platform=browser#configuring-the-scope
          //const sessionId = Cookie.get('sid');
          //if (sessionId) {
          //  scope.setUser({ id: sessionId });
          //}
        } else {
          scope.setTag('ssr', true);
          scope.setExtra('url', req.url);
          scope.setExtra('method', req.method);
          scope.setExtra('headers', req.headers);
          scope.setExtra('params', req.params);
          scope.setExtra('query', req.query);

          // TODO: find proper way to set session id from our tokens here
          // https://docs.sentry.io/enriching-error-data/scopes/?platform=browser#configuring-the-scope
          // On server-side we take session cookie directly from request
          //if (req.cookies.sid) {
          //  scope.setUser({ id: req.cookies.sid });
          //}
        }

        // Include info from React component which thrown the error within render
        if (errorInfo) {
          Object.keys(errorInfo).forEach((key) =>
            scope.setExtra(key, errorInfo[key]),
          );
        }

        // Include current isSupportedBrowser state
        if (typeof isSupportedBrowser === 'boolean') {
          scope.setExtra('isSupportedBrowser', isSupportedBrowser);
        }

        // Include current redux store state if available
        if (reduxStore) {
          scope.setExtra('reduxState', reduxStore.getState());
        }

        if (tags) {
          Object.keys(tags).forEach((key) => {
            scope.setTag(key, tags[key]);
          });
        }
      }
    });

    // Datadog error configuration
    const globalError = new Error(err?.message || 'Unknown error');
    globalError.stack =
      additionalData?.errorInfo?.componentStack || 'No stack trace available';
    globalError.cause = err;

    SentryLib.captureException(err);
  };

  return { Sentry: SentryLib, captureException };
};

/**
 * @exports {SentryConfig}
 */
export const { Sentry, captureException } = errorsLoggingConfig();
